import React, { useRef, useInsertionEffect, useCallback, useState, useEffect, useImperativeHandle } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useEventCallback = function (fn) {
    var handler = useRef(fn);
    useInsertionEffect(function () {
        handler.current = fn;
    }, [fn]);
    return useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return handler.current.apply(handler, args);
    }, []);
};

var easeOut = function (currentTime, startValue, changeInValue, duration) {
    currentTime /= duration;
    currentTime--;
    return changeInValue * (currentTime * currentTime * currentTime + 1) + startValue;
};
var easeOutQuartic = function (currentTime, startValue, changeInValue, duration) {
    currentTime /= duration;
    currentTime--;
    return -changeInValue * (currentTime * currentTime * currentTime * currentTime - 1) + startValue;
};
var easeInOutCubic = function (currentTime, startValue, changeInValue, duration) {
    currentTime /= duration;
    return (changeInValue * (currentTime < 0.5 ? 4 * currentTime * currentTime * currentTime : 1 - Math.pow(-2 * currentTime + 2, 3) / 2) +
        startValue);
};
var easeInOut = function (currentTime, startValue, changeInValue, duration) {
    currentTime /= duration / 2;
    if (currentTime < 1) {
        return (changeInValue / 2) * currentTime * currentTime + startValue;
    }
    currentTime--;
    return (-changeInValue / 2) * (currentTime * (currentTime - 2) - 1) + startValue;
};
function clampWithEasing(value, minValue, maxValue, easingDistance) {
    if (easingDistance === void 0) { easingDistance = 50; }
    if (value < minValue) {
        return minValue - Math.min(easingDistance, easeOut(minValue - value, 0, easingDistance, easingDistance * 4));
    }
    if (value > maxValue) {
        return Math.min(easingDistance, easeOut(value - maxValue, 0, easingDistance, easingDistance * 4));
    }
    return value;
}

var easing = /*#__PURE__*/Object.freeze({
    __proto__: null,
    clampWithEasing: clampWithEasing,
    easeInOut: easeInOut,
    easeInOutCubic: easeInOutCubic,
    easeOut: easeOut,
    easeOutQuartic: easeOutQuartic
});

var getCircularIndex = function (index, length) { return ((index % length) + length) % length; };
function interpolate(a, b, t) {
    return a * (1 - t) + b * t;
}

var math = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getCircularIndex: getCircularIndex,
    interpolate: interpolate
});

var clx = function () {
    var classes = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        classes[_i] = arguments[_i];
    }
    return classes.filter(Boolean).join(' ');
};

var registerMove = function (lastMoves, position) {
    return __spreadArray([{ position: position, ts: Date.now() }], lastMoves, true).slice(0, 5).filter(function (move) { return move.ts > Date.now() - 250; });
};
var getVelocity = function (lastMoves) {
    if (lastMoves.length < 2)
        return 0;
    var distance = lastMoves[0].position.x - lastMoves[lastMoves.length - 1].position.x;
    var time = lastMoves[0].ts - lastMoves[lastMoves.length - 1].ts;
    // velocity is movement per millisecond
    return distance / time;
};

// only render the given items once and stop sliding when reaching the beginning or end
var CYCLE_MODE_STOP = 'stop';
// endless render items, but align to the first and last item when using the slide left/right controls
var CYCLE_MODE_RESTART = 'restart';
// endless render items
var CYCLE_MODE_ENDLESS = 'endless';
var PREFERS_REDUCED_MOTION = typeof window !== 'undefined' ? !window.matchMedia('(prefers-reduced-motion)').matches : false;
var DRAG_EDGE_SNAP = 50;
var SLIDE_SNAPPING_DAMPING = 500;
var DRAG_SNAPPING_DAMPING = 1500;
var TileSlider = function (_a) {
    var items = _a.items, sliderRef = _a.sliderRef, _b = _a.tilesToShow, tilesToShow = _b === void 0 ? 6 : _b, _c = _a.cycleMode, cycleMode = _c === void 0 ? 'endless' : _c, _d = _a.spacing, spacing = _d === void 0 ? 12 : _d, _e = _a.showControls, showControls = _e === void 0 ? true : _e, _f = _a.animated, animated = _f === void 0 ? PREFERS_REDUCED_MOTION : _f, _g = _a.animationFn, animationFn = _g === void 0 ? easeOut : _g, _h = _a.pageStep, pageStep = _h === void 0 ? 'page' : _h, renderTile = _a.renderTile, renderLeftControl = _a.renderLeftControl, renderRightControl = _a.renderRightControl, renderPagination = _a.renderPagination, className = _a.className, _j = _a.classNames, classNames = _j === void 0 ? {} : _j, onSwipeStart = _a.onSwipeStart, onSwipeEnd = _a.onSwipeEnd, onSlideStart = _a.onSlideStart, onSlideEnd = _a.onSlideEnd, _k = _a.overscan, overscan = _k === void 0 ? tilesToShow : _k;
    var frameRef = useRef();
    var gesturesRef = useRef();
    var responsiveTileWidth = 100 / tilesToShow;
    var isMultiPage = items.length > tilesToShow;
    var pages = Math.ceil(items.length / tilesToShow);
    var needControls = showControls && isMultiPage;
    var _l = useState({
        index: 0,
        fromIndex: 0,
        toIndex: 0,
        sliding: false,
        page: 0,
    }), state = _l[0], setState = _l[1];
    var leftControlDisabled = cycleMode === 'stop' && state.index === 0;
    var rightControlDisabled = cycleMode === 'stop' && state.index === items.length - tilesToShow;
    var dynamicStepCount = pageStep === 'page' ? tilesToShow : 1;
    var stepCount = typeof pageStep === 'number' ? pageStep : dynamicStepCount;
    var leftOverscan = cycleMode === 'stop' ? Math.min(overscan, Math.abs(state.index)) : overscan;
    var rightOverscan = cycleMode === 'stop' ? Math.min(overscan, Math.abs(items.length - state.index - tilesToShow)) : overscan;
    var startIndex = isMultiPage ? state.index - leftOverscan : 0;
    var totalTiles = isMultiPage ? tilesToShow + (leftOverscan + rightOverscan) : items.length;
    var listOffset = isMultiPage ? (state.index - leftOverscan) * responsiveTileWidth : 0;
    var stableAnimationFn = useEventCallback(animationFn);
    var sliderDataRef = useRef({
        origin: { x: 0, y: 0 },
        moves: [],
        scrolling: false,
        cancelled: false,
        position: 0,
        velocity: 0,
        lastRenderedIndex: 0,
        animationId: 0,
        frameWidth: 0,
    });
    /**
     * Limit the given `index` to a valid index
     */
    var limitIndex = useCallback(function (index) {
        if (!isMultiPage) {
            index = 0;
        }
        if (cycleMode === CYCLE_MODE_STOP) {
            index = Math.max(0, Math.min(items.length - tilesToShow, index));
        }
        return index;
    }, [cycleMode, isMultiPage, items.length, tilesToShow]);
    /**
     * Calculate the current index based on the position of the slider frame
     */
    var calculateIndex = useCallback(function () {
        var tileWidth = sliderDataRef.current.frameWidth / tilesToShow;
        return limitIndex(Math.round((sliderDataRef.current.position / tileWidth) * -1));
    }, [limitIndex, tilesToShow]);
    /**
     * Get the slider frame position in pixels
     */
    var getSliderPosition = useEventCallback(function () {
        var _a;
        var transform = frameRef.current ? (_a = getComputedStyle(frameRef.current).transform) === null || _a === void 0 ? void 0 : _a.split(', ')[4] : '0';
        return transform ? parseInt(transform) : 0;
    });
    /**
     * Handle window resize event
     */
    var handleResize = useEventCallback(function () {
        cancelAnimationFrame(sliderDataRef.current.animationId);
        if (frameRef.current) {
            sliderDataRef.current.frameWidth = parseFloat(getComputedStyle(frameRef.current).width);
            frameRef.current.style.transform = "translateX(".concat(-responsiveTileWidth * state.index, "%)");
        }
    });
    // this effect resets the position when the tilesToShow changes
    useEffect(function () {
        handleResize();
    }, [handleResize, responsiveTileWidth, tilesToShow]);
    /**
     * Snapping will slide to the given index using the `animationFn` and `duration` for the easing. This is mainly used
     * when using the API, pagination or left/right controls to slide to a specific index.
     */
    var handleSnapping = useEventCallback(function (index, animationFn, duration) {
        if (duration === void 0) { duration = SLIDE_SNAPPING_DAMPING; }
        var tileWidth = sliderDataRef.current.frameWidth / tilesToShow;
        var from = getSliderPosition();
        var relativeToPosition = -responsiveTileWidth * index;
        var to = -(index * tileWidth);
        var change = to - from;
        var startTime = Date.now();
        var page = Math.floor(getCircularIndex(index, items.length) / tilesToShow);
        if (!animated) {
            setState(function (state) { return (__assign(__assign({}, state), { index: index, page: page, sliding: false })); });
            frameRef.current.style.transform = "translateX(".concat(relativeToPosition, "%)");
            onSlideEnd === null || onSlideEnd === void 0 ? void 0 : onSlideEnd({
                index: index,
                itemIndex: getCircularIndex(index, items.length),
                total: items.length,
                page: page,
                pages: pages,
            });
            return;
        }
        cancelAnimationFrame(sliderDataRef.current.animationId);
        setState(function (state) { return (__assign(__assign({}, state), { toIndex: index, fromIndex: state.index, sliding: true })); });
        var snappingDampening = function () {
            // interrupt
            if (!frameRef.current || sliderDataRef.current.scrolling)
                return;
            var currentTime = Date.now() - startTime;
            var position = animationFn(currentTime, from, change, duration);
            var currentIndex = calculateIndex();
            sliderDataRef.current.position = position;
            frameRef.current.style.transform = "translateX(".concat(position, "px)");
            if (currentTime <= duration) {
                sliderDataRef.current.animationId = requestAnimationFrame(snappingDampening);
            }
            else {
                frameRef.current.style.transform = "translateX(".concat(relativeToPosition, "%)");
                onSlideEnd === null || onSlideEnd === void 0 ? void 0 : onSlideEnd({
                    index: currentIndex,
                    itemIndex: getCircularIndex(currentIndex, items.length),
                    total: items.length,
                    page: page,
                    pages: pages,
                });
                setState(function (state) { return (__assign(__assign({}, state), { index: index, page: page, sliding: false })); });
            }
            if (sliderDataRef.current.lastRenderedIndex !== currentIndex) {
                sliderDataRef.current.lastRenderedIndex = currentIndex;
                setState(function (state) { return (__assign(__assign({}, state), { index: currentIndex, page: page })); });
            }
        };
        requestAnimationFrame(snappingDampening);
    });
    /**
     * The `handleVelocity` function is called to smoothly transition from a drag gesture to programmatic animation.
     * Based on the speed (velocity), the animation function and duration is calculated until the velocity reaches a given
     * threshold. When this happens, the velocity animation switches to a snapping animation to, also smoothly, align with a
     * tile.
     */
    var handleVelocity = useEventCallback(function () {
        var startVelocity = sliderDataRef.current.velocity * 16;
        // snap back to the current tile when the velocity is near zero
        if (Math.abs(startVelocity) < 1) {
            return handleSnapping(calculateIndex(), easeOutQuartic, 500);
        }
        // Swipe to prev/next (consider a velocity between -8 and 8 to be a swipe)
        // A velocity of 8 is little more than a gentle swipe
        if (Math.abs(startVelocity) < 8) {
            return handleSnapping(calculateIndex(), easeOutQuartic, SLIDE_SNAPPING_DAMPING);
        }
        // animation duration based on the velocity
        var startTime = Date.now();
        var tileWidth = sliderDataRef.current.frameWidth / tilesToShow;
        var extraDuration = Math.pow(Math.abs(startVelocity), 2) / 3.5;
        var totalDuration = DRAG_SNAPPING_DAMPING + extraDuration;
        var finished = false;
        var snappingStartTime = -1;
        var snappingDuration = 0;
        var snappingStartPosition = 0;
        var snappingTargetPosition = 0;
        cancelAnimationFrame(sliderDataRef.current.animationId);
        setState(function (state) { return (__assign(__assign({}, state), { fromIndex: state.index, sliding: true })); });
        var velocityDampening = function () {
            // interrupted by a touch gesture or the slider was unmounted
            if (!frameRef.current || sliderDataRef.current.scrolling)
                return;
            var currentTime = Date.now() - startTime;
            var currentIndex = calculateIndex();
            var page = Math.floor(getCircularIndex(currentIndex, items.length) / tilesToShow);
            var velocity = easeOutQuartic(currentTime, startVelocity, -startVelocity, totalDuration);
            // total duration of the snap animation from the startTime
            var totalDurationSnap = snappingStartTime + snappingDuration - startTime;
            // handle snapping to the precalculated tile index
            if (snappingStartTime !== -1) {
                var snappingProgress = Date.now() - snappingStartTime;
                sliderDataRef.current.position = easeOut(snappingProgress, snappingStartPosition, snappingTargetPosition - snappingStartPosition, snappingDuration);
                if (snappingProgress >= snappingDuration) {
                    finished = true;
                }
            }
            else {
                sliderDataRef.current.position += velocity;
                if (cycleMode === CYCLE_MODE_STOP) {
                    sliderDataRef.current.position = clampWithEasing(sliderDataRef.current.position, -tileWidth * (items.length - tilesToShow), 0, DRAG_EDGE_SNAP);
                }
            }
            // calculate the snapping values when the velocity drops below 10 OR when reaching a boundary in cycle mode stop
            // this ensures that we have a consistent speed to blend the snapping animation
            if ((Math.abs(velocity) <= 10 ||
                (cycleMode === CYCLE_MODE_STOP && (currentIndex <= 0 || currentIndex >= items.length - tilesToShow))) &&
                snappingStartTime === -1) {
                snappingStartTime = Date.now();
                var targetIndexFloat = -(sliderDataRef.current.position / tileWidth);
                var targetBuffer = 0.35;
                var targetIndex = limitIndex(velocity > 0 ? Math.floor(targetIndexFloat - targetBuffer) : Math.ceil(targetIndexFloat + targetBuffer));
                if (cycleMode === CYCLE_MODE_STOP) {
                    snappingTargetPosition = -(Math.max(0, Math.min(items.length - tilesToShow, targetIndex)) * tileWidth);
                }
                else {
                    snappingTargetPosition = -(targetIndex * tileWidth);
                }
                // this multiplier aligns pretty well maintaining the same velocity
                snappingDuration = Math.min(2000, Math.abs(snappingTargetPosition - sliderDataRef.current.position) * 5);
                snappingStartPosition = sliderDataRef.current.position;
            }
            // apply the position
            frameRef.current.style.transform = "translateX(".concat(sliderDataRef.current.position, "px)");
            if (currentTime >= Math.max(totalDuration, totalDurationSnap)) {
                finished = true;
            }
            if (!finished) {
                sliderDataRef.current.animationId = requestAnimationFrame(velocityDampening);
            }
            else {
                frameRef.current.style.transform = "translateX(".concat(-responsiveTileWidth * currentIndex, "%)");
                onSlideEnd === null || onSlideEnd === void 0 ? void 0 : onSlideEnd({
                    index: currentIndex,
                    itemIndex: getCircularIndex(currentIndex, items.length),
                    total: items.length,
                    page: page,
                    pages: pages,
                });
                setState(function (state) { return (__assign(__assign({}, state), { index: currentIndex, page: page, sliding: false })); });
            }
            if (sliderDataRef.current.lastRenderedIndex !== currentIndex) {
                sliderDataRef.current.lastRenderedIndex = currentIndex;
                setState(function (state) { return (__assign(__assign({}, state), { index: currentIndex, page: page })); });
            }
        };
        sliderDataRef.current.animationId = requestAnimationFrame(velocityDampening);
    });
    var slideToIndex = useCallback(function (index, closest) {
        if (closest === void 0) { closest = false; }
        index = limitIndex(index);
        var itemIndex = getCircularIndex(state.index, items.length);
        var page = Math.floor(itemIndex / tilesToShow);
        if (closest) {
            var toItemIndex = getCircularIndex(index, items.length);
            var delta = toItemIndex - itemIndex;
            index = state.index + delta;
        }
        if (!isMultiPage)
            return;
        setState(function (state) { return (__assign(__assign({}, state), { page: page })); });
        onSlideStart === null || onSlideStart === void 0 ? void 0 : onSlideStart({
            index: index,
            itemIndex: itemIndex,
            total: items.length,
            page: page,
            pages: pages,
        });
        handleSnapping(index, stableAnimationFn);
    }, [limitIndex, handleSnapping, isMultiPage, items.length, onSlideStart, pages, stableAnimationFn, state.index, tilesToShow]);
    var slideToPage = useCallback(function (page) {
        var pageTileIndex = page * stepCount;
        var delta = getCircularIndex(pageTileIndex, items.length) - getCircularIndex(state.index, items.length);
        var index = state.index + delta;
        slideToIndex(index);
    }, [items.length, slideToIndex, state.index, stepCount]);
    var slide = useCallback(function (direction) {
        var directionFactor = direction === 'right' ? 1 : -1;
        var slideStepCount = stepCount;
        if (cycleMode === CYCLE_MODE_RESTART) {
            var itemIndex = getCircularIndex(state.index, items.length);
            var diffToLast = items.length - tilesToShow - itemIndex;
            var diffToFirst = itemIndex;
            // slide to the first/last item when exceeding
            if (direction === 'right' && diffToLast !== 0)
                slideStepCount = Math.min(diffToLast, slideStepCount);
            if (direction === 'left' && diffToFirst !== 0)
                slideStepCount = Math.min(diffToFirst, slideStepCount);
        }
        var toIndex = state.index + slideStepCount * directionFactor;
        slideToIndex(toIndex);
    }, [cycleMode, items.length, slideToIndex, state.index, stepCount, tilesToShow]);
    useImperativeHandle(sliderRef, function () {
        return {
            slide: slide,
            slideToPage: slideToPage,
            slideToIndex: slideToIndex,
        };
    }, [slide, slideToIndex, slideToPage]);
    var handleTouchStart = useEventCallback(function (event) {
        sliderDataRef.current.origin = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
        };
        // reset data
        sliderDataRef.current.velocity = 0;
        sliderDataRef.current.moves = registerMove([], sliderDataRef.current.origin);
        sliderDataRef.current.position = getSliderPosition();
        sliderDataRef.current.scrolling = true;
        sliderDataRef.current.cancelled = false;
        onSwipeStart === null || onSwipeStart === void 0 ? void 0 : onSwipeStart();
        onSlideStart === null || onSlideStart === void 0 ? void 0 : onSlideStart({
            index: state.index,
            itemIndex: getCircularIndex(state.index, items.length),
            page: state.page,
            pages: pages,
            total: items.length,
        });
    });
    /**
     * This function returns the new slider position based on the given (drag) delta
     */
    var getSliderDragPosition = function (delta) {
        if (!isMultiPage)
            delta = clampWithEasing(delta, 0, 0, DRAG_EDGE_SNAP);
        var position = sliderDataRef.current.position + delta;
        if (isMultiPage && cycleMode === CYCLE_MODE_STOP) {
            var tileWidth = sliderDataRef.current.frameWidth / tilesToShow;
            var minPosition = -(items.length - tilesToShow) * tileWidth;
            var maxPosition = 0;
            position = clampWithEasing(position, minPosition, maxPosition, DRAG_EDGE_SNAP);
        }
        return position;
    };
    var handleTouchMove = useEventCallback(function (event) {
        var newPosition = {
            x: event.changedTouches[0].clientX,
            y: event.changedTouches[0].clientY,
        };
        var _a = sliderDataRef.current, origin = _a.origin, scrolling = _a.scrolling;
        sliderDataRef.current.moves = registerMove(sliderDataRef.current.moves, newPosition);
        // total movement
        var delta = newPosition.x - origin.x;
        var movementX = Math.abs(newPosition.x - origin.x);
        var movementY = Math.abs(newPosition.y - origin.y);
        if (movementX < movementY || sliderDataRef.current.cancelled) {
            sliderDataRef.current.cancelled = true;
            return;
        }
        if (movementX > movementY || scrolling) {
            event.preventDefault();
            event.stopPropagation();
            sliderDataRef.current.scrolling = true;
            frameRef.current.style.transform = "translateX(".concat(getSliderDragPosition(delta), "px)");
        }
    });
    var handleTouchEnd = useEventCallback(function (event) {
        var _a = sliderDataRef.current, origin = _a.origin, moves = _a.moves;
        var newPosition = {
            x: event.changedTouches[0].clientX,
            y: event.changedTouches[0].clientY,
        };
        // relative movement (velocity)
        var velocity = getVelocity(moves);
        var delta = newPosition.x - origin.x;
        var movementX = Math.abs(newPosition.x - origin.x);
        var movementY = Math.abs(newPosition.y - origin.y);
        sliderDataRef.current.scrolling = false;
        sliderDataRef.current.velocity = 0;
        if (sliderDataRef.current.cancelled || !isMultiPage) {
            return handleVelocity();
        }
        sliderDataRef.current.position = getSliderDragPosition(delta);
        // we slide when the movement was mostly horizontal
        if (movementX > movementY) {
            sliderDataRef.current.velocity = velocity;
        }
        onSwipeEnd === null || onSwipeEnd === void 0 ? void 0 : onSwipeEnd();
        handleVelocity();
    });
    useEffect(function () {
        var gesturesElement = gesturesRef.current;
        window.addEventListener('resize', handleResize);
        gesturesElement.addEventListener('touchstart', handleTouchStart);
        gesturesElement.addEventListener('touchmove', handleTouchMove, { passive: false });
        gesturesElement.addEventListener('touchend', handleTouchEnd);
        gesturesElement.addEventListener('touchcancel', handleTouchEnd);
        handleResize();
        return function () {
            window.removeEventListener('resize', handleResize);
            gesturesElement.removeEventListener('touchstart', handleTouchStart);
            gesturesElement.removeEventListener('touchstart', handleTouchStart);
            gesturesElement.removeEventListener('touchmove', handleTouchMove);
            gesturesElement.removeEventListener('touchend', handleTouchEnd);
            gesturesElement.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [handleResize, handleTouchEnd, handleTouchMove, handleTouchStart]);
    var renderTileContainer = function (index) {
        var itemIndex = getCircularIndex(index, items.length);
        var fromIndex = state.sliding ? Math.min(state.index, state.fromIndex, state.toIndex) : state.index;
        var toIndex = (state.sliding ? Math.max(state.index, state.fromIndex, state.toIndex) : state.index) + tilesToShow;
        var isVisible = index >= fromIndex && index < toIndex;
        return (React.createElement("li", { style: { width: "".concat(responsiveTileWidth, "%"), paddingLeft: spacing / 2, paddingRight: spacing / 2 }, className: clx(classNames.tile, isVisible ? 'TileSlider--visible' : 'TileSlider--hidden'), key: index, "aria-hidden": !isVisible }, renderTile({ item: items[itemIndex], itemIndex: itemIndex, isVisible: isVisible, index: index, slide: slide })));
    };
    var renderTiles = function () {
        return Array.from({ length: totalTiles }, function (_, index) { return renderTileContainer(startIndex + index); });
    };
    var renderLeftControlWrapper = function () {
        var content = renderLeftControl === null || renderLeftControl === void 0 ? void 0 : renderLeftControl({
            onClick: function () { return slide('left'); },
            disabled: leftControlDisabled,
        });
        return content && React.createElement("div", { className: clx('TileSlider-leftControl', classNames.leftControl) }, content);
    };
    var renderRightControlWrapper = function () {
        var content = renderRightControl === null || renderRightControl === void 0 ? void 0 : renderRightControl({
            onClick: function () { return slide('right'); },
            disabled: rightControlDisabled,
        });
        return content && React.createElement("div", { className: clx('TileSlider-rightControl', classNames.rightControl) }, content);
    };
    return (React.createElement("div", { className: clx('TileSlider', className) },
        needControls && renderLeftControlWrapper(),
        React.createElement("div", { className: clx('TileSlider-gestures', classNames.gestures), style: { marginLeft: -(spacing / 2), marginRight: -(spacing / 2) }, ref: gesturesRef },
            React.createElement("ul", { className: clx('TileSlider-list', classNames.list), ref: frameRef, style: { left: "calc(".concat(listOffset, "%)") } }, renderTiles())),
        needControls && renderRightControlWrapper(), renderPagination === null || renderPagination === void 0 ? void 0 :
        renderPagination({
            index: state.index,
            itemIndex: getCircularIndex(state.index, items.length),
            total: items.length,
            page: state.page,
            pages: pages,
            slide: slide,
            slideToPage: slideToPage,
            slideToIndex: slideToIndex,
        })));
};

var memoizedMediaQueries = [];
var getMediaQueries = function () {
    if (!memoizedMediaQueries.length && typeof window !== 'undefined') {
        var xs = matchMedia('screen and (max-width: 479px)'); // mobile
        var sm = matchMedia('screen and (min-width: 480px) and (max-width: 767px)'); // tablet
        var md = matchMedia('screen and (min-width: 768px) and (max-width: 1023px)'); // tablet large
        var lg = matchMedia('screen and (min-width: 1024px) and (max-width: 1199px)'); // desktop
        memoizedMediaQueries = [xs, sm, md, lg];
    }
    return memoizedMediaQueries;
};
var getSize = function () {
    var _a = getMediaQueries(), xs = _a[0], sm = _a[1], md = _a[2], lg = _a[3];
    if (!xs)
        return 'md'; // default to sm screen size
    if (xs.matches)
        return 'xs';
    if (sm.matches)
        return 'sm';
    if (md.matches)
        return 'md';
    if (lg.matches)
        return 'lg';
    else
        return 'xl';
};
var useResponsiveSize = function (sizes) {
    var _a = useState(getSize()), size = _a[0], setSize = _a[1];
    useEffect(function () {
        var mediaQueries = getMediaQueries();
        var handleChange = function () {
            setSize(getSize());
        };
        mediaQueries.forEach(function (matchMedia) { return matchMedia.addEventListener('change', handleChange); });
        return function () {
            mediaQueries.forEach(function (matchMedia) { return matchMedia.removeEventListener('change', handleChange); });
        };
    }, []);
    return sizes.map(function (config) { return config[size]; });
};

export { CYCLE_MODE_ENDLESS, CYCLE_MODE_RESTART, CYCLE_MODE_STOP, TileSlider, easing, math, useResponsiveSize };
//# sourceMappingURL=index.esm.js.map
